<template>
  <div class="flex flex-col w-full h-full overflow-y-auto bg-surface-50">
    <div class="flex flex-col w-full md:max-w-[560px] px-3.5 pb-6">
      <!-- Identity header -->
      <div class="flex flex-col items-center pt-4 pb-4">
        <div class="relative">
          <img
            v-if="selectedProfileImage"
            :src="selectedProfileImage"
            alt="תמונת פרופיל"
            class="w-[74px] h-[74px] rounded-full object-cover"
          />
          <div
            v-else
            class="w-[74px] h-[74px] rounded-full bg-primary-50 text-primary flex items-center justify-center text-2xl font-semibold"
          >{{ userInitials }}</div>
          <button
            type="button"
            class="absolute bottom-0 left-0 w-[26px] h-[26px] rounded-full bg-primary border-2 border-white flex items-center justify-center"
            aria-label="החלפת תמונת פרופיל"
            @click="imageUploadRef?.pickImage()"
          >
            <i class="pi pi-pencil text-[10px] text-white" />
          </button>
        </div>
        <!-- Hidden host for the pick → crop → upload flow (the crop modal teleports to body) -->
        <image-upload
          ref="imageUploadRef"
          key="profile-upload"
          class="hidden"
          :uploadFunction="onSelectProfileImage"
          :ratio="1"
          :imageUrl="selectedProfileImage"
          origin="profile"
          @remove="onRemoveProfileImage"
        />
        <div class="text-base font-semibold mt-2.5">{{ generalSettings.fullName }}</div>
        <div class="text-xs text-surface-500 mt-0.5">{{ roleLine }}</div>
        <button
          v-if="selectedProfileImage"
          type="button"
          class="text-[11px] text-red-700 font-medium mt-1.5"
          @click="onRemoveProfileImage"
        >הסרת תמונה</button>
      </div>

      <settings-card>
        <div class="py-3 border-b border-surface-100">
          <label class="block text-[11px] font-semibold text-surface-500 mb-1.5" for="fullName">שם מלא (יוצג בקביעת התור)</label>
          <InputText id="fullName" v-model="generalSettings.fullName" class="w-full" />
        </div>
        <div class="py-3 border-b border-surface-100">
          <div class="text-[11px] font-semibold text-surface-500 mb-1">אימייל</div>
          <div class="text-sm text-surface-800" style="direction: ltr; text-align: right">{{ generalSettings.email }}</div>
        </div>
        <div class="flex items-center gap-3 py-3">
          <div class="flex-1">
            <div class="text-[11px] font-semibold text-surface-500 mb-1">סיסמה</div>
            <div class="text-sm text-surface-700 tracking-widest">••••••••</div>
          </div>
          <button
            type="button"
            class="text-xs font-medium text-primary-600 flex items-center gap-1.5"
            :disabled="resetEmailLoading"
            @click="sendResetPasswordEmail"
          >
            <i v-if="resetEmailLoading" class="pi pi-spinner pi-spin text-xs" />
            שינוי
          </button>
        </div>
      </settings-card>

      <template v-if="isMainClient">
        <settings-group-label>העסק שלי</settings-group-label>
        <settings-card>
          <div class="flex items-center gap-3 py-3 border-b border-surface-100">
            <div class="w-9 h-9 rounded-xl bg-blue-100 flex items-center justify-center flex-none">
              <i class="pi pi-globe text-base text-blue-700" />
            </div>
            <div class="flex-1 min-w-0 cursor-pointer" @click="openSite">
              <div class="text-sm font-medium">כתובת האתר</div>
              <div class="text-[11px] text-surface-500 whitespace-nowrap overflow-hidden text-ellipsis" style="direction: ltr; text-align: right">{{ siteUrlDisplay }}</div>
            </div>
            <button
              type="button"
              class="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-surface-100 flex-none"
              @click="copySiteUrl"
            >
              <i class="pi pi-copy text-xs text-surface-600" />
              <span class="text-[11.5px] font-medium text-surface-700">העתקה</span>
            </button>
          </div>
          <div class="flex items-center gap-3 py-3">
            <div class="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center flex-none">
              <i class="pi pi-star text-base text-amber-700" />
            </div>
            <div class="flex-1 text-sm font-medium">מסלול</div>
            <span
              class="text-[11px] font-semibold px-2.5 py-1 rounded-full flex-none"
              :class="generalSettings.plan === Plans.PREMIUM ? 'bg-amber-100 text-amber-700' : 'bg-orange-100 text-orange-700'"
            >{{ generalSettings.plan === Plans.PREMIUM ? '#הכל_כלול' : '#משתדרגים_בחינם' }}</span>
          </div>
        </settings-card>

        <settings-group-label>אזור מסוכן</settings-group-label>
        <settings-card>
          <div
            v-if="!confirmDelete"
            class="flex items-center gap-3 py-3 cursor-pointer"
            @click="startRemoveAccount"
          >
            <i class="pi pi-trash text-base text-red-700 flex-none" />
            <span class="flex-1 text-sm font-medium text-red-700">מחיקת חשבון</span>
            <i class="pi pi-chevron-left text-sm text-surface-300 flex-none" />
          </div>
          <div v-else class="py-3">
            <div class="text-sm font-semibold text-red-700">מחיקת חשבון</div>
            <div class="text-xs text-surface-500 mt-1 mb-3 leading-relaxed">שימו לב — מחיקת החשבון היא סופית. כל הנתונים, התורים והלקוחות יימחקו.</div>
            <div class="flex gap-2">
              <Button label="הבנתי, למחוק את החשבון" severity="danger" size="small" :loading="deleteInProgress" @click="removeAccount" />
              <Button label="ביטול" severity="secondary" size="small" @click="confirmDelete = false" />
            </div>
          </div>
        </settings-card>
      </template>

      <settings-save-bar :dirty="dirty" :saved="saved" :loading="editNameBtnLoading" @save="updateProfile" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ISettingsGeneral, Plans } from '@torim-app/types';

import { useToast } from 'vue-toastification';
import { computed, reactive, ref } from 'vue';
import { SettingsApi } from '@app/api/admin/settings-api';
import { AdminApi } from '@app/api/admin/admin-api';
import Session from 'supertokens-web-js/recipe/session';
import { sendPasswordResetEmail } from 'supertokens-web-js/recipe/emailpassword';
import { AuthRouteNames } from '@app/router/route-names';
import { useNavigation } from '@app/composables/navigation';
import { useAdminSettingsStore } from '@app/stores/admin-settings';
import { useAdminStore } from '@app/stores';
import { trackEvent } from '@app/composables/posthog';
import { openLinkNewTab } from '@torim-app/utils';
import { Capacitor } from '@capacitor/core';
import { Clipboard } from '@capacitor/clipboard';
import ImageUpload from '@app/components/admin/components/ImageUpload.vue';
import SettingsCard from './ui/SettingsCard.vue';
import SettingsGroupLabel from './ui/SettingsGroupLabel.vue';
import SettingsSaveBar from './ui/SettingsSaveBar.vue';
import { useSettingsForm } from './ui/settings-form';

const settingsStore = useAdminSettingsStore();
const adminStore = useAdminStore();

const toast = useToast();
const { goTo } = useNavigation();

const generalSettings = reactive<ISettingsGeneral>(<ISettingsGeneral>{});
const editNameBtnLoading = ref(false);
const selectedProfileImage = ref<string>();
const imageUploadRef = ref<InstanceType<typeof ImageUpload>>();
const confirmDelete = ref(false);
const deleteInProgress = ref(false);
const resetEmailLoading = ref(false);

const { dirty, saved, takeSnapshot, flashSaved } = useSettingsForm(() => ({ fullName: generalSettings.fullName }));

const isMainClient = computed(() => adminStore.getMainClient);
const settingsData = computed(() => settingsStore.getSettings);
const userInitials = computed(() =>
  (generalSettings.fullName || '').split(' ').filter(Boolean).slice(0, 2).map(w => w[0]).join('')
);
const roleLine = computed(() => {
  const role = isMainClient.value ? 'מנהל/ת ראשי/ת' : 'חבר/ת צוות';
  const business = adminStore.getBusinessTitle;
  return business ? `${role} · ${business}` : role;
});
const siteUrlDisplay = computed(() => (generalSettings.siteUrl || '').replace(/^https?:\/\//, ''));

const initData = () => {
  try {
    Object.assign(generalSettings, settingsData.value.general);
    generalSettings.profileImage = settingsData.value.general?.profileImage;
    selectedProfileImage.value = settingsData.value.general?.profileImage;
    takeSnapshot();
  } catch (e) {
    console.error(e);
  }
};

const updateProfile = async () => {
  trackEvent('settings_profile_updated');
  try {
    const body: Partial<Pick<ISettingsGeneral, 'fullName' | 'profileImage'>> = {}
    if (generalSettings.fullName !== settingsData.value.general.fullName)
      body.fullName = generalSettings.fullName;
    if (selectedProfileImage.value !== settingsData.value.general.profileImage)
      body.profileImage = selectedProfileImage.value ?? '';

    if (Object.entries(body).length > 0) {
      if (body.fullName) editNameBtnLoading.value = true;
      await SettingsApi.updateGeneralPageSettings(body);
      flashSaved();
      await settingsStore.fetchBusinessSettings()
      initData();
    }
  } catch (e) {
    console.error(e)
    toast.error('לא הצלנו לעדכן');
  }
  editNameBtnLoading.value = false;
}

const openSite = () => {
  if (generalSettings.siteUrl) openLinkNewTab(generalSettings.siteUrl);
};

// Password changes go through the existing SuperTokens reset-email flow
// (same API the forgot-password screen uses — the email link opens ResetPassword).
const sendResetPasswordEmail = async () => {
  trackEvent('settings_password_reset_clicked');
  resetEmailLoading.value = true;
  try {
    const response = await sendPasswordResetEmail({
      formFields: [{ id: 'email', value: generalSettings.email }]
    });
    if (response.status === 'OK')
      toast.success('שלחנו לך אימייל עם קישור להחלפת הסיסמה 📩');
    else
      toast.error('לא הצלחנו לשלוח את האימייל, נסו שוב מאוחר יותר');
  } catch (e) {
    console.error(e);
    toast.error('לא הצלחנו לשלוח את האימייל, נסו שוב מאוחר יותר');
  }
  resetEmailLoading.value = false;
};

const copySiteUrl = async () => {
  trackEvent('settings_site_url_copied');
  if (Capacitor.isNativePlatform()) {
    await Clipboard.write({ string: generalSettings.siteUrl });
  } else {
    await navigator.clipboard.writeText(generalSettings.siteUrl);
  }
  toast.success('הקישור הועתק בהצלחה 😃');
};

const startRemoveAccount = () => {
  trackEvent('delete_account_clicked');
  confirmDelete.value = true;
};

const removeAccount = async () => {
  deleteInProgress.value = true;
  try {
    await AdminApi.removeAccount();
    await Session.signOut();
    goTo(AuthRouteNames.AdminLogin);
    toast.success('המשתמש שלך נמחק בהצלחה!');
  } catch (e) {
    console.error(e)
    toast.error('לא הצלנו למחוק את המשתמש');
  }
  deleteInProgress.value = false;
}

const onSelectProfileImage = (imageUrl: string) => {
  trackEvent('profile_image_uploaded');
  if (imageUrl) {
    selectedProfileImage.value = imageUrl;
    updateProfile()
  }
};

const onRemoveProfileImage = () => {
  selectedProfileImage.value = undefined;
  updateProfile();
};

// Desktop surfaces the save action as a header-end button (see Configurations.vue);
// exposed under the same shape as the other settings sub-pages.
defineExpose({ saveChanges: updateProfile, loadingCta: editNameBtnLoading, dirty });

initData();

</script>
